// src/components/MyRenders.jsx - FIXED VERSION with working preview and real-time progress
import React, { useState, useEffect } from 'react';
import { Plus, Download, Eye, Play, Pause, AlertCircle, AlertTriangle, Clock, CheckCircle, RefreshCw, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../services/api';
import { useNotification } from './NotificationContext';
import { subscribeToUserJobUpdates, subscribeToJobUpdates } from '../services/socketService';

// Enhanced render status component with preview and real-time progress
const EnhancedRenderStatus = ({ render, onRefresh }) => {
  const { t } = useLanguage();
  const { addToast } = useNotification();
  const [isDownloading, setIsDownloading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [localProgress, setLocalProgress] = useState(render.progress || 0);

  // ✅ FIX 1: Subscribe to real-time progress updates for this specific job
  useEffect(() => {
    if (render._id && (render.status === 'rendering' || render.status === 'queued')) {
      console.log(`🔄 Subscribing to progress updates for job: ${render._id}`);
      
      const unsubscribe = subscribeToJobUpdates(render._id, (updatedJob) => {
        console.log(`📡 Progress update for ${render._id}:`, updatedJob);
        
        // Update local progress state
        if (typeof updatedJob.progress === 'number') {
          setLocalProgress(updatedJob.progress);
        }
        
        // Call parent refresh if status changed
        if (updatedJob.status !== render.status) {
          onRefresh(render._id);
        }
      });

      return () => {
        console.log(`🔌 Unsubscribing from job updates: ${render._id}`);
        unsubscribe();
      };
    }
  }, [render._id, render.status, onRefresh]);

  // ✅ Update local progress when render prop changes
  useEffect(() => {
    if (typeof render.progress === 'number') {
      setLocalProgress(render.progress);
    }
  }, [render.progress]);

  const getStatusIcon = () => {
    switch (render.status) {
      case 'complete':
      case 'completed':
        return <CheckCircle size={16} className="text-green-400" />;
      case 'rendering':
        return <Play size={16} className="text-yellow-400" />;
      case 'queued':
        return <Clock size={16} className="text-blue-400" />;
      case 'failed':
        return <AlertCircle size={16} className="text-red-400" />;
      default:
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'queued': return 'bg-yellow-100 text-yellow-800';
      case 'rendering': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      'Express': 'bg-purple-100 text-purple-800',
      'Priority': 'bg-orange-100 text-orange-800', 
      'Standard': 'bg-blue-100 text-blue-800',
      'Economy': 'bg-green-100 text-green-800'
    };
    return colors[priority] || colors['Standard'];
  };

  const handleDownload = async (renderId, renderName) => {
    try {
      setIsDownloading(true);
      console.log(`🔽 Starting download for render: ${renderId}`);
      const result = await api.getRenderOutput(renderId);

      if (result.success) {
        if (result.type === 'direct_download') {
          addToast && addToast('Download started successfully!', 'success');
        } else if (result.data && result.data.outputUrl) {
          const link = document.createElement('a');
          link.href = result.data.outputUrl;
          link.download = result.data.fileName || `${renderName || 'render'}.mp4`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          addToast && addToast('Download started!', 'success');
        }
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('❌ Download error:', error);
      const errorMessage = error.message || 'Download failed. Please try again.';
      addToast && addToast(errorMessage, 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  // ✅ FIX 2: Enhanced preview function that works like Dashboard
  const handlePreview = async () => {
    if (showPreview && previewUrl) {
      setShowPreview(false);
      setPreviewUrl(null);
      return;
    }

    try {
      console.log(`👁️ Loading preview for job: ${render._id}`);
      
      // For completed renders, try to get the video file directly
      if ((render.status === 'completed' || render.status === 'complete') && render.outputFilePath) {
        try {
          console.log(`🎬 Trying to load completed video as preview`);
          
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/renders/${render._id}/download`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Accept': 'application/json, video/mp4, */*'
            },
            credentials: 'include'
          });

          if (response.ok) {
            const contentType = response.headers.get('content-type');
            
            if (contentType && contentType.includes('application/json')) {
              // It's a JSON response with signed URL
              const data = await response.json();
              if (data.success && data.data && data.data.outputUrl) {
                setPreviewUrl(data.data.outputUrl);
                setShowPreview(true);
                return;
              }
            } else {
              // It's a direct file response
              const blob = await response.blob();
              const blobUrl = URL.createObjectURL(blob);
              setPreviewUrl(blobUrl);
              setShowPreview(true);
              
              // Clean up blob URL after 5 minutes
              setTimeout(() => {
                URL.revokeObjectURL(blobUrl);
              }, 5 * 60 * 1000);
              return;
            }
          }
        } catch (videoError) {
          console.warn('Video preview failed, trying image preview:', videoError.message);
        }
      }
      
      // Fallback: try to get preview image
      if (render.previewImagePath) {
        try {
          const previewResponse = await api.getRenderPreview(render._id);
          if (previewResponse.success && previewResponse.data.previewUrl) {
            setPreviewUrl(previewResponse.data.previewUrl);
            setShowPreview(true);
            return;
          }
        } catch (imageError) {
          console.warn('Image preview failed:', imageError.message);
        }
      }
      
      // If all else fails
      addToast && addToast('Preview not available for this render', 'warning');
      
    } catch (error) {
      console.error('❌ Preview failed:', error);
      addToast && addToast(`Preview failed: ${error.message}`, 'error');
    }
  };

  return (
    <div className="space-y-3">
      {/* Status and Actions Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(render.status)}`}>
            {getStatusIcon()}
            <span className="ml-1 capitalize">{render.status}</span>
          </span>

          {/* ✅ FIX 3: Enhanced progress bar with real-time updates */}
          {(render.status === 'queued' || render.status === 'rendering') && (
            <div className="flex items-center space-x-2">
              <div className="relative w-32 h-3 bg-gray-700 rounded-full overflow-hidden">
                {/* Base progress bar */}
                <div
                  className={`absolute h-full rounded-full transition-all duration-500 ease-out ${
                    render.status === 'rendering'
                      ? 'bg-gradient-to-r from-yellow-400 to-yellow-500'
                      : render.status === 'queued'
                      ? 'bg-gradient-to-r from-blue-400 to-blue-500'
                      : 'bg-red-500'
                  }`}
                  style={{ 
                    width: `${Math.max(3, Math.min(100, localProgress || 0))}%`,
                    minWidth: render.status === 'rendering' ? '12px' : '8px'
                  }}
                >
                  {/* Animated stripes for rendering */}
                  {render.status === 'rendering' && (
                    <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.3)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.3)_50%,rgba(255,255,255,0.3)_75%,transparent_75%,transparent)] bg-[length:20px_20px] animate-[progress-stripes_1s_linear_infinite] rounded-full"></div>
                  )}
                </div>

                {/* Moving icon */}
                {render.status === 'rendering' && (
                  <Play
                    size={12}
                    className="absolute -top-2 text-yellow-400 drop-shadow-lg transition-all duration-500 ease-out"
                    style={{ left: `calc(${Math.max(0, Math.min(100, localProgress || 0))}% - 6px)` }}
                  />
                )}
              </div>
              
              <span className="text-xs text-gray-400 font-medium min-w-[35px]">
                {localProgress || 0}%
              </span>
            </div>
          )}

          {/* Queue position for queued jobs */}
          {render.status === 'queued' && render.queuePosition && (
            <span className="text-xs text-blue-400">Position: {render.queuePosition}</span>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center space-x-2">
          {(render.status === 'complete' || render.status === 'completed') && (
            <>
              <button
                onClick={handlePreview}
                className="p-1 text-gray-400 hover:text-white rounded transition-colors"
                title={showPreview ? "Hide Preview" : "Preview"}
              >
                {showPreview ? <X size={16} /> : <Eye size={16} />}
              </button>
              <button
                onClick={() => handleDownload(render._id, render.name)}
                disabled={isDownloading}
                className={`p-1 rounded transition-colors ${
                  isDownloading
                    ? 'text-gray-500 cursor-not-allowed'
                    : 'text-gray-400 hover:text-green-400'
                }`}
                title={isDownloading ? 'Downloading...' : 'Download'}
              >
                {isDownloading ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <Download size={16} />
                )}
              </button>
            </>
          )}

          <button
            onClick={() => onRefresh(render._id)}
            className="p-1 text-gray-400 hover:text-white rounded transition-colors"
            title="Refresh status"
          >
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {(render.status === 'queued' || render.status === 'rendering') && (
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center">
            {render.status === 'queued' && (
              <div className="flex items-center text-blue-400">
                <div className="w-1 h-1 bg-blue-400 rounded-full mr-2 animate-ping"></div>
                <span>{t('waiting')} {render.priority?.toLowerCase() || 'standard'} {t('queue')}</span>
              </div>
            )}
            {render.status === 'rendering' && (
              <div className="flex items-center text-yellow-400">
                <div className="w-1 h-1 bg-yellow-400 rounded-full mr-2 animate-ping"></div>
                <span>
                  {t('renderingframe')} {Math.floor(((localProgress || 0) / 100) * (render.duration || 1) * 24)}/{(render.duration || 1) * 24}
                </span>
              </div>
            )}
          </div>
          
          {/* Time remaining estimate */}
          {render.status === 'rendering' && localProgress > 5 && (
            <div className="text-gray-400">
              {(() => {
                const estimated = Math.round(((100 - localProgress) / Math.max(localProgress, 1)) * 2);
                return estimated > 60 
                  ? `~${Math.round(estimated / 60)}h left`
                  : `~${estimated}m left`;
              })()}
            </div>
          )}
        </div>
      )}

      {/* ✅ FIX 4: Enhanced Preview Section */}
      {showPreview && previewUrl && (
        <div className="mt-3 p-3 bg-gray-700 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-white">{t('Preview')}</span>
            <button
              onClick={() => {
                setShowPreview(false);
                setPreviewUrl(null);
                // Clean up blob URL if it exists
                if (previewUrl && previewUrl.startsWith('blob:')) {
                  URL.revokeObjectURL(previewUrl);
                }
              }}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={16} />
            </button>
          </div>
          
          {/* Determine if it's a video or image */}
          {previewUrl.includes('.mp4') || previewUrl.startsWith('blob:') ? (
            <video 
              src={previewUrl} 
              controls 
              className="w-full h-48 rounded object-cover"
              onError={(e) => {
                console.error('Video preview failed to load');
                setShowPreview(false);
                addToast && addToast('Video preview failed to load', 'error');
              }}
            >
              {t('videoerror')}
            </video>
          ) : (
            <img 
              src={previewUrl} 
              alt="Render preview" 
              className="w-full h-48 object-cover rounded"
              onError={(e) => {
                console.error('Image preview failed to load');
                setShowPreview(false);
                addToast && addToast('Preview image failed to load', 'error');
              }}
            />
          )}
        </div>
      )}

      {/* Error Message for Failed Jobs */}
      {render.status === 'failed' && render.renderLogs && render.renderLogs.length > 0 && (
        <div className="mt-3 p-3 bg-red-900/20 border border-red-700 rounded-lg">
          <div className="flex items-center mb-2">
            <AlertTriangle size={16} className="text-red-400 mr-2" />
            <span className="text-red-400 font-medium text-sm">{t('renderfailed')}</span>
          </div>
          <p className="text-sm text-red-300">
            {render.renderLogs[render.renderLogs.length - 1]?.message || 'Unknown error occurred'}
          </p>
        </div>
      )}
    </div>
  );
};

// Main component with real-time updates
const MyRenders = ({ renderQueue, isLoading, setActiveTab }) => {
  const { t } = useLanguage();
  const { addToast } = useNotification();
  const [renders, setRenders] = useState(renderQueue || []);
  const [refreshing, setRefreshing] = useState(false);

  // ✅ FIX 5: Subscribe to user job updates for real-time status changes
  useEffect(() => {
    if (!renders || renders.length === 0) return;

    // Get user ID from the first render or from localStorage/context
    const userId = renders[0]?.user || localStorage.getItem('userId');
    if (!userId) return;

    console.log(`🔄 Subscribing to user job updates for: ${userId}`);

    const unsubscribe = subscribeToUserJobUpdates(userId, (jobUpdate) => {
      console.log(`📡 User job update received:`, jobUpdate);
      
      setRenders((prev) =>
        prev.map((r) => {
          if (r._id === jobUpdate._id || r._id === jobUpdate.jobId) {
            return { 
              ...r, 
              ...jobUpdate,
              // Ensure we preserve essential fields
              _id: r._id,
              user: r.user,
              name: r.name || jobUpdate.name,
              status: jobUpdate.status || r.status,
              progress: typeof jobUpdate.progress === 'number' ? jobUpdate.progress : r.progress
            };
          }
          return r;
        })
      );
    });

    return () => {
      console.log('🔌 Unsubscribing from user job updates');
      unsubscribe();
    };
  }, [renders]);

  useEffect(() => {
    setRenders(renderQueue || []);
  }, [renderQueue]);

  // Auto-refresh for active renders
  useEffect(() => {
    let interval;
    const hasActiveRenders = renders.some(r => r.status === 'rendering' || r.status === 'queued');
    if (hasActiveRenders) {
      interval = setInterval(refreshRenders, 10000); // Refresh every 10 seconds
    }
    return () => interval && clearInterval(interval);
  }, [renders]);

  const refreshRenders = async () => {
    try {
      setRefreshing(true);
      const response = await api.getRenderQueue();
      if (response.success) {
        setRenders(response.data);
      }
    } catch (error) {
      console.error('Error refreshing renders:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const refreshSingleRender = async (renderId) => {
    try {
      const response = await api.getRenderJob(renderId);
      if (response.success) {
        setRenders(prev =>
          prev.map(r => (r._id === renderId ? response.data : r))
        );
      }
    } catch (error) {
      console.error('Error refreshing render:', error);
    }
  };

  const handleNewRenderClick = () => {
    if (typeof setActiveTab === 'function') {
      setActiveTab('upload');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEstimatedTime = (render) => {
    if ((render.status === 'complete' || render.status === 'completed') && render.startTime && render.endTime) {
      const duration = (new Date(render.endTime) - new Date(render.startTime)) / 1000 / 60;
      return `${Math.round(duration)} min`;
    }
    if (render.status === 'rendering' && render.startTime) {
      const elapsed = (new Date() - new Date(render.startTime)) / 1000 / 60;
      return `${Math.round(elapsed)} min elapsed`;
    }
    return '-';
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('myRenders')}</h1>
          <p className="text-gray-400 mt-1">{t('allYourProjects')}</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={refreshRenders}
            disabled={refreshing}
            className="flex items-center px-3 py-2 text-gray-400 hover:text-white transition-colors"
          >
            <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            className="bg-red-600 text-white px-4 py-2 rounded flex items-center hover:bg-red-700 action-button"
            onClick={handleNewRenderClick}
          >
            <Plus size={16} className="mr-2" /> {t('newRender')}
          </button>
        </div>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <div className="bg-gray-800 rounded-lg shadow p-6 text-center">
            <div className="animate-pulse flex justify-center">
              <div className="h-4 w-4 bg-red-600 rounded-full mx-1"></div>
              <div className="h-4 w-4 bg-red-600 rounded-full mx-1 animate-pulse delay-150"></div>
              <div className="h-4 w-4 bg-red-600 rounded-full mx-1 animate-pulse delay-300"></div>
            </div>
            <p className="text-gray-400 mt-4">{t('loadingRenders')}</p>
          </div>
        ) : renders && renders.length > 0 ? (
          <div className="bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="bg-gray-700 px-6 py-3 border-b border-gray-600">
              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-300">
                <div className="col-span-3">{t('name')}</div>
                <div className="col-span-2">{t('resolution')}</div>
                <div className="col-span-1">{t('duration')}</div>
                <div className="col-span-2">{t('created')}</div>
                <div className="col-span-1">{t('time')}</div>
                <div className="col-span-3">{t('status')}</div>
              </div>
            </div>

            <div className="divide-y divide-gray-700">
              {renders.map(render => (
                <div
                  key={render._id || render.id}
                  className="px-6 py-4 hover:bg-gray-700/50 transition-colors"
                >
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-3">
                      <h3 className="font-medium text-white truncate">{render.name}</h3>
                      <p className="text-sm text-gray-400 truncate">{render.quality} {t('quality')}</p>
                    </div>

                    <div className="col-span-2">
                      <span className="text-gray-300">{render.resolution}</span>
                    </div>

                    <div className="col-span-1">
                      <span className="text-gray-300">{render.duration}s</span>
                    </div>

                    <div className="col-span-2">
                      <span className="text-gray-300 text-sm">
                        {formatDate(render.createdAt || render.created)}
                      </span>
                    </div>

                    <div className="col-span-1">
                      <span className="text-gray-300 text-sm">
                        {getEstimatedTime(render)}
                      </span>
                    </div>

                    <div className="col-span-3">
                      <EnhancedRenderStatus render={render} onRefresh={refreshSingleRender} />
                    </div>
                  </div>

                  {/* Debug logs (last 5 entries) */}
                  {render.renderLogs && render.renderLogs.length > 0 && (
                    <div className="mt-3 p-3 bg-gray-900/30 border border-gray-600/30 rounded">
                      <h4 className="text-gray-300 font-medium text-sm mb-2">
                        {t('logs')} ({render.status})
                      </h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto text-xs font-mono">
                        {render.renderLogs.slice(-5).map((log, idx) => (
                          <p
                            key={idx}
                            className={
                              log.level === "error"
                                ? "text-red-400"
                                : log.level === "warn"
                                ? "text-yellow-400"
                                : "text-gray-300"
                            }
                          >
                            {new Date(log.timestamp).toLocaleTimeString()} → {log.message}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-lg shadow p-6 text-center">
            <p className="text-gray-400">{t('noRenders')}</p>
            <button
              className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              onClick={handleNewRenderClick}
            >
              {t('createNewRender')}
            </button>
          </div>
        )}
      </div>

      {renders.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">{t('totalrenders')}</h3>
            <p className="text-2xl font-bold text-white">{renders.length}</p>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">{t('completed')}</h3>
            <p className="text-2xl font-bold text-green-400">
              {renders.filter(r => r.status === 'completed' || r.status === 'complete').length}
            </p>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">{t('inprogress')}</h3>
            <p className="text-2xl font-bold text-yellow-400">
              {renders.filter(r => r.status === 'rendering' || r.status === 'queued').length}
            </p>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">{t('failed')}</h3>
            <p className="text-2xl font-bold text-red-400">
              {renders.filter(r => r.status === 'failed').length}
            </p>
          </div>
        </div>
      )}

      {/* ✅ Add CSS for progress bar animations */}
      <style jsx>{`
        @keyframes progress-stripes {
          0% {
            background-position: 0 0;
          }
          100% {
            background-position: 20px 0;
          }
        }
      `}</style>
    </div>
  );
};

export default MyRenders;